import React from "react";
import { useStore } from "react-redux";
import TextField from "@material-ui/core/TextField";
import NumberField from "src/components/NumberField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SalePlatformField from "src/components/SalePlatformField";
import { linkedItemsSelector } from "src/store/transaction/selector";
import { Row } from "./Row";
import { DateInput } from "./DateInput";
import type { FormProps } from "./types";

export function DefaultForm({
  errors,
  values,
  loading,
  handleChange,
  setValues,
  dateRef,
  showDate = true,
  hideAmount,
  hideDescription,
}: FormProps) {
  const store = useStore();
  const isUpdate = !!values.id;

  return (
    <>
      {hideDescription ? null : (
        <Row text="A note to remind you what was purchased.">
          <TextField
            error={errors.description ? true : false}
            helperText={errors.description}
            fullWidth
            variant="outlined"
            margin="dense"
            label="Description"
            value={values.description}
            disabled={loading}
            onChange={handleChange("description")}
          />
        </Row>
      )}
      {hideAmount ? null : (
        <Row>
          <NumberField
            error={errors.amount ? true : false}
            helperText={errors.amount}
            fullWidth
            variant="outlined"
            margin="dense"
            label="Amount"
            value={Math.abs(values.amount || 0)}
            disabled={loading}
            onChange={(event) => {
              const amount = event.target.value;
              setValues((values) => {
                const multiplier =
                  Object.is(values.amount, -0) || (values.amount || 0) < 0
                    ? -1
                    : 1;
                return {
                  ...values,
                  amount: (amount || 0) * multiplier,
                };
              });
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            fixed={2}
          />
        </Row>
      )}
      {showDate ? (
        <Row className="date">
          <DateInput
            ref={dateRef}
            value={values.date}
            onChange={(event, t) => {
              let value = event.target.value;
              if (!isUpdate && value?.getTime()) {
                value.setUTCHours(12, 0, 0, 0);
              }

              setValues((values) => {
                const newValues = { ...values, date: value };
                if (Array.isArray(newValues.items)) {
                  if (newValues.items.length === 0 || !newValues.id) {
                    const items = linkedItemsSelector(store.getState(), {
                      date: value,
                    });
                    newValues.items = items.map(({ type, item }) => ({
                      type,
                      id: item.id,
                    }));
                  }
                }

                return newValues;
              });
              handleChange("date");
            }}
            loading={loading}
            error={!!errors.date}
            helperText={errors.date}
          />
        </Row>
      ) : null}
      <Row text="The card or account used to make the purchase.">
        <SalePlatformField
          type="all"
          fullWidth
          disabled={loading}
          value={{
            id: values.bank_account_id,
            name: values.integration_name,
          }}
          onChange={(value) => {
            setValues((v) => {
              if (value)
                return {
                  ...v,
                  integration_name: value.name,
                  bank_account_id: value.id,
                };
              const { integration_name, ...rest } = v;
              return rest;
            });
          }}
          error={!!errors.integration_name}
          helperText={errors.integration_name}
          margin="dense"
          variant="outlined"
          label="Bank Account"
          required
        />
      </Row>
    </>
  );
}
